import { useTypeWire } from "@typewirets/react";
import { Calculator } from "lucide-react";
import type React from "react";
import { useCallback, useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Campaign } from "@/hooks/api/use-campaigns";
import { useTrackedBrowserStorage } from "@/hooks/use-tracked-browser-storage";
import { ASTSerializerWire } from "@/lib/formula-parser.wire";
import type {
  FormulaColumnDefinition,
  FormulaTableColumn,
} from "@/types/formula-column";

// Re-export types for consumers
export type { CampaignWithFormulaColumns } from "@/types/formula-column";

interface UseFormulaColumnsProps {
  storageKey?: string;
}

// Helper function to generate a unique ID for formula columns
function generateFormulaId(): string {
  const now = new Date();

  // Format: f_YYYYMMDD_HHMMSS_random
  const date =
    now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, "0") +
    now.getDate().toString().padStart(2, "0");

  const time =
    now.getHours().toString().padStart(2, "0") +
    now.getMinutes().toString().padStart(2, "0") +
    now.getSeconds().toString().padStart(2, "0");

  // Generate 6-character random string (alphanumeric)
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let random = "";
  for (let i = 0; i < 6; i++) {
    random += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return `f_${date}_${time}_${random}`;
}

/**
 * Hook for managing formula column definitions.
 *
 * Provides functionality to create, update, and delete formula column definitions
 * stored in localStorage. Formula evaluation is handled server-side via API calls.
 * Formulas are stored as parsed AST for efficiency.
 *
 * @example
 * ```typescript
 * const {
 *   formulaColumnDefinitions,
 *   createFormulaColumn,
 *   updateFormulaColumn,
 *   deleteFormulaColumn
 * } = useFormulaColumns();
 *
 * // Create a new formula column with parsed AST
 * const newColumn = createFormulaColumn({
 *   name: "Revenue Per Click",
 *   formulaAST: parsedAST, // Pre-parsed AST
 *   dataType: "currency",
 *   description: "Revenue divided by total clicks"
 * });
 * ```
 */
// Default storage key for campaigns formula columns
const DEFAULT_CAMPAIGNS_FORMULA_KEY =
  "campaigns::list::columns::formula-columns";

export function useFormulaColumns({
  storageKey = DEFAULT_CAMPAIGNS_FORMULA_KEY,
}: UseFormulaColumnsProps = {}) {
  // Store formula column definitions in localStorage with organization scoping
  const [rawFormulaColumnDefinitions, setRawFormulaColumnDefinitions] =
    useTrackedBrowserStorage<FormulaColumnDefinition[]>(storageKey, [], {
      storage: "localStorage",
      // Add migration for default campaigns key
      ...(storageKey === DEFAULT_CAMPAIGNS_FORMULA_KEY && {
        migration: {
          from: {
            key: "campaign-formula-columns",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true,
        },
      }),
    });

  // Create AST serializer instance using TypeWire
  const astSerializer = useTypeWire(ASTSerializerWire);

  // Transform raw data to ensure dates are proper Date objects
  const formulaColumnDefinitions = useMemo(() => {
    return (rawFormulaColumnDefinitions || []).map((def) => ({
      ...def,
      createdAt:
        def.createdAt instanceof Date ? def.createdAt : new Date(def.createdAt),
      updatedAt:
        def.updatedAt instanceof Date ? def.updatedAt : new Date(def.updatedAt),
    }));
  }, [rawFormulaColumnDefinitions]);

  // Wrapper for setFormulaColumnDefinitions
  const setFormulaColumnDefinitions = useCallback(
    (updater: React.SetStateAction<FormulaColumnDefinition[]>) => {
      setRawFormulaColumnDefinitions(updater);
    },
    [setRawFormulaColumnDefinitions],
  );

  /**
   * Create a new formula column definition
   */
  const createFormulaColumn = useCallback(
    (
      columnData: Omit<
        FormulaColumnDefinition,
        "id" | "createdAt" | "updatedAt"
      >,
    ) => {
      // Generate unique ID
      const id = generateFormulaId();

      // Create new column
      const newColumn: FormulaColumnDefinition = {
        ...columnData,
        id,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setFormulaColumnDefinitions(
        (prev: FormulaColumnDefinition[] | undefined) => [
          ...(prev || []),
          newColumn,
        ],
      );
      return newColumn;
    },
    [setFormulaColumnDefinitions],
  );

  /**
   * Update an existing formula column definition
   *
   * @param id - Formula column ID to update
   * @param updates - Partial updates to apply
   */
  const updateFormulaColumn = useCallback(
    (
      id: string,
      updates: Partial<Omit<FormulaColumnDefinition, "id" | "createdAt">>,
    ) => {
      setFormulaColumnDefinitions(
        (prev: FormulaColumnDefinition[] | undefined) =>
          (prev || []).map((col: FormulaColumnDefinition) =>
            col.id === id ? { ...col, ...updates, updatedAt: new Date() } : col,
          ),
      );
    },
    [setFormulaColumnDefinitions],
  );

  /**
   * Delete a formula column definition
   *
   * @param id - Formula column ID to delete
   */
  const deleteFormulaColumn = useCallback(
    (id: string) => {
      setFormulaColumnDefinitions(
        (prev: FormulaColumnDefinition[] | undefined) =>
          (prev || []).filter((col: FormulaColumnDefinition) => col.id !== id),
      );
    },
    [setFormulaColumnDefinitions],
  );

  /**
   * Get formula definitions in a format suitable for API transmission
   * Converts AST to formula string for server processing
   * Only includes valid formulas (skips ones with parsing errors)
   *
   * @returns Array of valid formula definitions ready for API calls
   */
  const getFormulasForAPI = useCallback(() => {
    return formulaColumnDefinitions
      .filter((def) => def.isValid !== false && !def.parseError)
      .map((def) => ({
        id: def.id,
        name: def.name,
        formula: astSerializer.astToFormula(def.formulaAST), // Convert AST to formula string
        dataType: def.dataType,
        description: def.description,
      }));
  }, [formulaColumnDefinitions, astSerializer]);

  /**
   * Get formula string for display purposes
   *
   * @param columnId - Formula column ID
   * @returns Formula string for display, or error message for invalid formulas
   */
  const getFormulaString = useCallback(
    (columnId: string): string => {
      const column = formulaColumnDefinitions.find(
        (def) => def.id === columnId,
      );
      if (!column) return "";

      if (column.parseError || column.isValid === false) {
        return `Error: ${column.parseError || "Invalid formula"}`;
      }

      return astSerializer.astToFormula(column.formulaAST);
    },
    [formulaColumnDefinitions, astSerializer],
  );

  // Convert formula column definitions to table columns for display
  const formulaTableColumns = useMemo((): FormulaTableColumn[] => {
    return (formulaColumnDefinitions || []).map(
      (columnDef: FormulaColumnDefinition) => ({
        key: `formula_${columnDef.id}`,
        title: columnDef.name,
        tooltip: columnDef.description,
        columnType: "formula" as const,
        icon: <Calculator className="h-3 w-3" />,
        sortable: true,
        filterable: false, // Formula columns aren't filterable for now
        defaultSortDirection: "desc" as const,
        nullSort: "last" as const,
        render: (campaign: Campaign) => {
          // Check for parsing errors in the column definition first
          if (columnDef.parseError || columnDef.isValid === false) {
            return (
              <span
                className="text-destructive text-sm"
                title={columnDef.parseError || "Invalid formula"}
              >
                Parse Error
              </span>
            );
          }

          // Get formula result from campaign data (server provides this in the formulas object)
          const formulaKey = columnDef.id; // Remove formula_ prefix since server response doesn't include it
          const campaignData = campaign as Campaign & {
            formulas?: Record<string, unknown>;
          };
          const value = campaignData.formulas?.[formulaKey];

          if (value === null || value === undefined) {
            return <span className="text-muted-foreground">-</span>;
          }

          // Format value based on data type
          const formatValue = (
            val: unknown,
            dataType: FormulaColumnDefinition["dataType"],
          ): string => {
            if (val === null || val === undefined) return "-";

            switch (dataType) {
              case "currency":
                return (val as number).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                });
              case "percentage":
                return `${(val as number).toFixed(1)}%`;
              case "number":
                return (val as number).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                });
              case "date":
                return new Date(val as string).toLocaleDateString();
              case "boolean":
                return val ? "Yes" : "No";
              default:
                return String(val);
            }
          };

          const formattedContent = (
            <span className="font-medium">
              {formatValue(value, columnDef.dataType)}
            </span>
          );

          return columnDef.description ? (
            <Tooltip>
              <TooltipTrigger asChild>{formattedContent}</TooltipTrigger>
              <TooltipContent className="max-w-[20rem]">
                <p>{columnDef.description}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            formattedContent
          );
        },
        formulaColumn: columnDef,
      }),
    );
  }, [formulaColumnDefinitions]);

  return {
    // Data
    formulaColumnDefinitions,
    formulaTableColumns,

    // Actions
    createFormulaColumn,
    updateFormulaColumn,
    deleteFormulaColumn,
    getFormulasForAPI,
    getFormulaString,

    // Computed
    hasFormulaColumns: (formulaColumnDefinitions || []).length > 0,
  };
}
